import getIn from 'transmute/getIn';
import * as Identifiable from '../protocol/Identifiable';
import {
  fromJS,
  Iterable,
  Map as ImmutableMap,
  Record as ImmutableRecord,
} from 'immutable';
import PropertyValueRecord from '../property/PropertyValueRecord';
import { ObjectType } from '../types/ObjectType';
import identity from 'transmute/identity';
import { ObjectRecord } from '../types/ObjectRecord';
import { CRM_OBJECT } from '../constants/CrmObjectType';
import { PropertyValueRecordType } from '../types/PropertyValueRecordType';

const EMPTY_TOSTRING = '-';

const mapProperty = (
  record?: ObjectRecord<any>,
  property?: string
): string | undefined => {
  if (!record || !property) {
    return undefined;
  }
  return (record as unknown as Iterable<string, string>).getIn([
    'properties',
    property,
    'value',
  ]);
};

const join = (arr: string[]): string | null => {
  const joined = arr.join(' ').trim();
  if (joined === '') {
    return null;
  }
  return joined;
};

type ObjectIdentifier = string[] | ((record: ObjectRecord<any>) => string);

const prepStrings = (
  record: ObjectRecord<any>,
  {
    primary,
    secondary,
  }: { primary?: ObjectIdentifier; secondary?: ObjectIdentifier }
): { primary: string; secondary: string } => {
  if (!primary) {
    primary = [];
  }
  if (!secondary) {
    secondary = [];
  }

  const mapPropertyForRecord = mapProperty.bind(null, record);
  const primaryString =
    typeof primary === 'function'
      ? primary(record)
      : join(primary.map(mapPropertyForRecord).filter(identity) as string[]);
  const secondaryString =
    typeof secondary === 'function'
      ? secondary(record)
      : join(secondary.map(mapPropertyForRecord).filter(identity) as string[]);

  return { primary: primaryString!, secondary: secondaryString! };
};

export interface MakeObjectRecordOptions<T extends Record<string, any>> {
  readonly idKey: readonly string[];
  readonly recordName: string;
  readonly objectType: ObjectType | typeof CRM_OBJECT;
  readonly defaults: T;
}

// This interface is the shared fields that are available on all objects, both
// legacy objects and new CrmObjects. It represents the most basic level of an
// object definition and all fields in it should always be defined on an objects.
//
// This interface should directly mirror it's pure JS counterpart
// BaseObjectContents
export interface BaseObjectRecordContents {
  properties: ImmutableMap<string, PropertyValueRecordType>;
}

export interface ObjectRecordFactory<T extends Object>
  extends Omit<ImmutableRecord.Factory<T>, 'toString'> {
  // hack to make TS pick up the fact that these factories are callable
  (
    ...args: Parameters<ImmutableRecord.Factory<T>>
  ): ReturnType<ImmutableRecord.Factory<T>>;
  readonly _idKey: readonly string[];
  readonly _objectType: ObjectType | typeof CRM_OBJECT;
  toString(record?: ObjectRecord<T>): string;
  toStringExpanded(record?: ObjectRecord<T>): string;
  fromJS(json?: any): ObjectRecord<T>;
}

export default function makeObjectRecord<T extends BaseObjectRecordContents>(
  { idKey, recordName, objectType, defaults }: MakeObjectRecordOptions<T>,
  toStringOptions = {}
): ObjectRecordFactory<T> {
  const BaseObjectRecordFactory = ImmutableRecord(defaults, recordName);
  const ObjectRecordFactory: ObjectRecordFactory<T> = Object.assign(
    BaseObjectRecordFactory,
    {
      _idKey: idKey,
      _objectType: objectType,
      toString(record?: ObjectRecord<T>) {
        if (!record) {
          return EMPTY_TOSTRING;
        }
        const { primary, secondary } = prepStrings(record, toStringOptions);

        return primary || secondary || EMPTY_TOSTRING;
      },
      toStringExpanded(record?: ObjectRecord<T>): string {
        if (!record) {
          return EMPTY_TOSTRING;
        }
        const { primary, secondary } = prepStrings(record, toStringOptions);
        let returnString = '';

        if (primary) {
          returnString = primary;
          if (secondary) {
            return `${returnString} (${secondary})`;
          }
          return returnString;
        }

        if (secondary) {
          return secondary;
        }

        return EMPTY_TOSTRING;
      },
      fromJS(json?: any) {
        if (json === null || json === undefined) {
          return json;
        }
        let immutable = fromJS(json);
        if (immutable.has('properties')) {
          immutable = immutable.update(
            'properties',
            (properties: (typeof PropertyValueRecord)[]) =>
              properties && properties.map
                ? properties.map(PropertyValueRecord.fromJS)
                : properties
          );
        }
        return ObjectRecordFactory(immutable);
      },
    }
  );

  // getIn's type requires a mutable array
  Identifiable.getId.implement(ObjectRecordFactory, getIn(idKey as string[]));

  return ObjectRecordFactory;
}
